import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { auth } from "../../firebase";
import Sidebar from "./Sidebar";
import WorkspaceSidebar from "../team_workspaces/WorkspaceSidebar";
import "../../css/DocumentationMainPage.css";
import "../../css/AdvancedSearch.css";

const BASE_URL = "https://bluebirddocumentationadmin.pythonanywhere.com";

function AdvancedSearch() {
    const { customDomain } = useParams();
    const isWorkspaceMode = Boolean(customDomain);
    const navigate = useNavigate();

    const [uid, setUid] = useState(null);
    const [authResolved, setAuthResolved] = useState(false);
    const [workspaceId, setWorkspaceId] = useState(null);
    const [workspaceName, setWorkspaceName] = useState(null);
    const [workspaceTheme, setWorkspaceTheme] = useState(null);

    const [query, setQuery] = useState("");
    const [searchIn, setSearchIn] = useState({ title: true, description: true, content: false });
    const [docType, setDocType] = useState("all");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [updatedFrom, setUpdatedFrom] = useState("");
    const [updatedTo, setUpdatedTo] = useState("");
    const [includeArchived, setIncludeArchived] = useState(false);

    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [searchError, setSearchError] = useState(null);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setUid(user.uid);
            } else {
                setUid(null);
            }
            setAuthResolved(true);
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (!isWorkspaceMode || !uid) return;
        const fetchWorkspace = async () => {
            try {
                const user = auth.currentUser;
                if (!user) return;
                const token = await user.getIdToken();
                const response = await fetch(`${BASE_URL}/workspaces/domain/${customDomain}/`, {
                    headers: {
                        'Authorization': token,
                        'Auth-Token': `Bearer ${token}`,
                        'Requesting-User-Uid': uid,
                    }
                });
                if (response.ok) {
                    const data = await response.json();
                    setWorkspaceId(data.id);
                    setWorkspaceName(data.name);
                    setWorkspaceTheme(data.theme || "Default");
                }
            } catch (e) {
                console.error("Error fetching workspace for search:", e);
            }
        };
        fetchWorkspace();
    }, [isWorkspaceMode, uid, customDomain]);

    const toggleSearchIn = (field) => {
        setSearchIn(prev => ({ ...prev, [field]: !prev[field] }));
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!uid) return;

        const selectedFields = Object.entries(searchIn).filter(([, v]) => v).map(([k]) => k);
        if (selectedFields.length === 0) {
            setSearchError("Please select at least one field to search in.");
            return;
        }

        setLoading(true);
        setSearchError(null);
        setSearched(false);

        try {
            const user = auth.currentUser;
            if (!user) throw new Error("Not authenticated");
            const token = await user.getIdToken();

            const params = new URLSearchParams({
                search_in: selectedFields.join(','),
                doc_type: docType,
                include_archived: String(includeArchived),
            });
            if (query) params.set('q', query);
            if (dateFrom) params.set('date_from', dateFrom);
            if (dateTo) params.set('date_to', dateTo);
            if (updatedFrom) params.set('updated_from', updatedFrom);
            if (updatedTo) params.set('updated_to', updatedTo);

            const url = isWorkspaceMode && workspaceId
                ? `${BASE_URL}/workspaces/${workspaceId}/search/?${params}`
                : `${BASE_URL}/documentation/search-personal/?${params}`;

            const response = await fetch(url, {
                headers: {
                    'Authorization': token,
                    'Auth-Token': `Bearer ${token}`,
                    'Requesting-User-Uid': uid,
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Search failed");
            }

            const data = await response.json();
            setResults(data.results || []);
            setSearched(true);
        } catch (err) {
            console.error("Search error:", err);
            setSearchError(err.message || "An error occurred during search.");
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setQuery('');
        setSearchIn({ title: true, description: true, content: false });
        setDocType('all');
        setDateFrom('');
        setDateTo('');
        setUpdatedFrom('');
        setUpdatedTo('');
        setIncludeArchived(false);
        setResults([]);
        setSearched(false);
        setSearchError(null);
    };

    const handleResultClick = (result) => {
        if (isWorkspaceMode && customDomain) {
            navigate(`/workspace/${customDomain}/${result.type}/${result.id}`);
        } else {
            navigate(`/documentation/${result.type}/${result.name}/${result.author}`);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric'
        });
    };

    if (authResolved && !uid) {
        const sidebar = isWorkspaceMode
            ? <WorkspaceSidebar customDomain={customDomain} workspaceId={workspaceId} workspaceTheme={workspaceTheme} />
            : <Sidebar />;
        return (
            <div className="DocumentationMainPage__div">
                {sidebar}
                <div className="DocumentationMainPage__main-content">
                    <p>Please log in to use advanced search.</p>
                </div>
            </div>
        );
    }

    const sidebar = isWorkspaceMode
        ? <WorkspaceSidebar customDomain={customDomain} workspaceId={workspaceId} workspaceTheme={workspaceTheme} />
        : <Sidebar />;

    return (
        <div className={`DocumentationMainPage__div${isWorkspaceMode ? ' AdvancedSearch__workspace-bg' : ''}`}>
            {sidebar}
            <div className="DocumentationMainPage__main-content AdvancedSearch__content">
                <div className="AdvancedSearch__header">
                    <button className="AdvancedSearch__back-btn" onClick={() => navigate(-1)}>
                        <i className="fas fa-arrow-left"></i> Back
                    </button>
                    <h1>Advanced Search</h1>
                    <p className="AdvancedSearch__subtitle">
                        {isWorkspaceMode
                            ? `Searching in: ${workspaceName || customDomain}`
                            : "Searching your personal documentation"}
                    </p>
                </div>

                <form onSubmit={handleSearch} className="AdvancedSearch__form">
                    <div className="AdvancedSearch__row">
                        <div className="AdvancedSearch__field AdvancedSearch__field--wide">
                            <label className="AdvancedSearch__label">Search Query</label>
                            <input
                                type="text"
                                value={query}
                                onChange={e => setQuery(e.target.value)}
                                placeholder="Enter keywords... (leave empty to list all documents)"
                                className="AdvancedSearch__input"
                                autoFocus
                            />
                        </div>
                        <div className="AdvancedSearch__field">
                            <label className="AdvancedSearch__label">Document Type</label>
                            <select
                                value={docType}
                                onChange={e => setDocType(e.target.value)}
                                className="AdvancedSearch__select"
                            >
                                <option value="all">All Types</option>
                                <option value="space">Spaces Only</option>
                                <option value="page">Pages Only</option>
                            </select>
                        </div>
                    </div>

                    <div className="AdvancedSearch__row">
                        <div className="AdvancedSearch__field">
                            <label className="AdvancedSearch__label">Search In</label>
                            <div className="AdvancedSearch__checkboxes">
                                {[
                                    { key: 'title', label: 'Title' },
                                    { key: 'description', label: 'Description' },
                                    { key: 'content', label: 'Content', note: '(slower)' },
                                ].map(({ key, label, note }) => (
                                    <label key={key} className="AdvancedSearch__checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={searchIn[key]}
                                            onChange={() => toggleSearchIn(key)}
                                        />
                                        <span>{label}</span>
                                        {note && <span className="AdvancedSearch__note">{note}</span>}
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="AdvancedSearch__field AdvancedSearch__field--archive">
                            <label className="AdvancedSearch__checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={includeArchived}
                                    onChange={e => setIncludeArchived(e.target.checked)}
                                />
                                <span>Include Archived Documents</span>
                            </label>
                        </div>
                    </div>

                    <div className="AdvancedSearch__section-title">Date Filters</div>
                    <div className="AdvancedSearch__row">
                        <div className="AdvancedSearch__field">
                            <label className="AdvancedSearch__label">Created After</label>
                            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="AdvancedSearch__input" />
                        </div>
                        <div className="AdvancedSearch__field">
                            <label className="AdvancedSearch__label">Created Before</label>
                            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="AdvancedSearch__input" />
                        </div>
                        <div className="AdvancedSearch__field">
                            <label className="AdvancedSearch__label">Last Updated After</label>
                            <input type="date" value={updatedFrom} onChange={e => setUpdatedFrom(e.target.value)} className="AdvancedSearch__input" />
                        </div>
                        <div className="AdvancedSearch__field">
                            <label className="AdvancedSearch__label">Last Updated Before</label>
                            <input type="date" value={updatedTo} onChange={e => setUpdatedTo(e.target.value)} className="AdvancedSearch__input" />
                        </div>
                    </div>

                    <div className="AdvancedSearch__actions">
                        <button
                            type="submit"
                            className="AdvancedSearch__search-btn"
                            disabled={loading || (isWorkspaceMode && !workspaceId)}
                        >
                            {loading
                                ? <><i className="fas fa-spinner fa-spin"></i> Searching...</>
                                : <><i className="fas fa-search"></i> Search</>
                            }
                        </button>
                        <button type="button" className="AdvancedSearch__clear-btn" onClick={handleClear}>
                            Clear
                        </button>
                    </div>
                </form>

                {searchError && (
                    <div className="AdvancedSearch__error">
                        <i className="fas fa-exclamation-circle"></i> {searchError}
                    </div>
                )}

                {searched && !loading && (
                    <div className="AdvancedSearch__results">
                        <div className="AdvancedSearch__results-header">
                            <span className="AdvancedSearch__results-count">
                                {results.length === 0
                                    ? 'No results found'
                                    : `${results.length} result${results.length !== 1 ? 's' : ''} found`}
                            </span>
                        </div>
                        {results.length > 0 && (
                            <ul className="AdvancedSearch__results-list">
                                {results.map((result, idx) => (
                                    <li
                                        key={idx}
                                        className="AdvancedSearch__result-item"
                                        onClick={() => handleResultClick(result)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={e => e.key === 'Enter' && handleResultClick(result)}
                                    >
                                        <div className="AdvancedSearch__result-header">
                                            <div className="AdvancedSearch__result-title-row">
                                                <span className={`AdvancedSearch__type-badge AdvancedSearch__type-badge--${result.type}`}>
                                                    <i className={`fas ${result.type === 'space' ? 'fa-folder' : 'fa-file-alt'}`}></i>
                                                    {result.type.charAt(0).toUpperCase() + result.type.slice(1)}
                                                </span>
                                                <span className="AdvancedSearch__result-name">{result.name}</span>
                                                {result.is_archived && (
                                                    <span className="AdvancedSearch__archived-badge">
                                                        <i className="fas fa-box-archive"></i> Archived
                                                    </span>
                                                )}
                                                {result.is_public && (
                                                    <span className="AdvancedSearch__public-badge">
                                                        <i className="fas fa-globe"></i> Public
                                                    </span>
                                                )}
                                            </div>
                                            <div className="AdvancedSearch__result-meta">
                                                <span>Created: {formatDate(result.created_at)}</span>
                                                <span>Updated: {formatDate(result.updated_at)}</span>
                                                {result.views != null && (
                                                    <span><i className="fas fa-eye"></i> {result.views} views</span>
                                                )}
                                                {result.author && isWorkspaceMode && (
                                                    <span><i className="fas fa-user"></i> {result.author}</span>
                                                )}
                                            </div>
                                        </div>
                                        {result.description && (
                                            <p className="AdvancedSearch__result-description">{result.description}</p>
                                        )}
                                        {result.content_snippet && (
                                            <p className="AdvancedSearch__result-snippet">
                                                <i className="fas fa-quote-left"></i> {result.content_snippet}
                                            </p>
                                        )}
                                        {(result.space || result.parent_page) && (
                                            <div className="AdvancedSearch__result-breadcrumb">
                                                {result.space && (
                                                    <span><i className="fas fa-folder"></i> {result.space.name}</span>
                                                )}
                                                {result.parent_page && (
                                                    <>
                                                        <i className="fas fa-chevron-right"></i>
                                                        <span>{result.parent_page.name}</span>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                        {!isWorkspaceMode && result.parent_page && (
                                            <div className="AdvancedSearch__result-breadcrumb">
                                                <i className="fas fa-level-up-alt fa-rotate-90"></i>
                                                <span>Parent: {result.parent_page}</span>
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default AdvancedSearch;
